// Owner 2026-10-19 (requirement 70, v2.10): the voice of the production screen. No imports, so Jest can test it.
//
// Opening chime: our own short tone sequences made with Web Audio (no audio files, no copy of any
// station/airport/brand jingle). Three choices + a firmer one for a QC reject.

export const NADA = {
  DINGDONG: { label: "Ding-dong (2 nada)", not: [[784, 0.45], [622, 0.7]] },
  TIGA: { label: "Tiga nada naik", not: [[523, 0.3], [659, 0.3], [784, 0.6]] },
  EMPAT: { label: "Empat nada", not: [[659, 0.28], [523, 0.28], [587, 0.28], [784, 0.6]] },
};
// reject: lower, faster, repeated — clearly different from the friendly chimes
export const NADA_TEGAS = { label: "Tegas (reject)", not: [[440, 0.16], [330, 0.16], [440, 0.16], [330, 0.4]] };

// v2.16-A (requirement 75 butir 9b): one short, soft note when work reaches a station — never spoken
export const NADA_PINDAH = { label: "Pindah pos (1 nada pendek)", not: [[988, 0.22]] };

export function putarPindah(ctx, volume = 70) {
  if (!ctx || volume <= 0) return 0;
  const [[f, lama]] = NADA_PINDAH.not;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = "sine"; o.frequency.value = f;
  o.connect(g); g.connect(ctx.destination);
  const t = ctx.currentTime;
  const puncak = Math.max(0.0002, Math.min(1, volume / 100) * 0.4);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(puncak, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + lama);
  o.start(t); o.stop(t + lama + 0.02);
  return Math.round(lama * 1000);
}

// The notes as a plan: [{ f, mulai, lama }] in seconds, plus the total length.
export function rencanaNada(nada, tegas = false) {
  const n = tegas ? NADA_TEGAS : NADA[nada] || NADA.DINGDONG;
  let t = 0;
  const not = n.not.map(([f, lama]) => {
    const x = { f, mulai: t, lama };
    t += lama * (tegas ? 1 : 0.85);  // friendly chimes overlap a little, like a bell
    return x;
  });
  const akhir = Math.max(...not.map((x) => x.mulai + x.lama));
  return { not, lama: akhir };
}

// Play a chime on an AudioContext (created after a button press). Returns the length in ms.
export function putarNada(ctx, { nada = "DINGDONG", volume = 70, tegas = false } = {}) {
  const r = rencanaNada(nada, tegas);
  if (!ctx || volume <= 0) return 0;
  const puncak = Math.max(0.0002, Math.min(1, volume / 100) * 0.5);
  r.not.forEach(({ f, mulai, lama }) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = tegas ? "square" : "sine";
    o.frequency.value = f;
    o.connect(g); g.connect(ctx.destination);
    const t = ctx.currentTime + mulai;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(tegas ? puncak * 0.5 : puncak, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + lama);
    o.start(t); o.stop(t + lama + 0.02);
  });
  return Math.round(r.lama * 1000);
}

// Long announcements are spoken in parts of whole sentences: some TV browsers stop a single long
// utterance after ±15 seconds. The parts are spoken one after another, so nothing is cut.
export function potongUcapan(teks, maks = 220) {
  const kalimat = String(teks || "").split(/(?<=[.!?:])\s+/).map((x) => x.trim()).filter(Boolean);
  const out = [];
  let kini = "";
  for (const k of kalimat) {
    if (kini && (kini + " " + k).length > maks) { out.push(kini); kini = k; } else kini = kini ? `${kini} ${k}` : k;
  }
  if (kini) out.push(kini);
  return out;
}

// Seconds a TV may need for a text (fallback when the browser never says it finished).
export const perkiraanDetik = (teks) => Math.ceil(String(teks || "").length / 12) + 5;

// Fill-in words in a template that the type does not know (the server refuses them too).
export function isianAsing(teks, boleh) {
  const ada = [...String(teks || "").matchAll(/\{([a-z_]+)\}/g)].map((m) => m[1]);
  return [...new Set(ada.filter((x) => !(boleh || []).includes(x)))];
}

// A stray brace outside a fill-in word (refused by the server).
export const kurungLepas = (teks) => /[{}]/.test(String(teks || "").replace(/\{[a-z_]+\}/g, ""));
